import { useImperativeHandle, useMemo, useRef, useState } from "react";

import { UseAutocompleteParams } from "./Autocomplete.types";

export const useAutocomplete = <V, R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseAutocompleteParams<V, R>) => {
  const autocompleteRef = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<V | null>(value ?? defaultValue ?? null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: autocompleteRef.current,
        value: state,
        setValue: (v: V) => {
          setState(v);
        },
        reset: () => {
          setState(null as unknown as V);
        },
      };
    }) as unknown as () => R,
    [state]
  );

  const onChangeHandler = (newValue: V | null) => {
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue);
  };

  const onClearStateHandler = () => {
    if (typeof value === "undefined") {
      setState(null);
    }
    onChange?.(null);
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state) as V,
    [value, state]
  );

  return {
    autocompleteRef,
    valueState,
    onChangeHandler,
    onClearStateHandler,
  };
};
